import type { ProjectDeploymentInfo } from '~/routes/api.deploy.$project.($id)';

export function getDeployInformationMessage(info: NonNullable<ProjectDeploymentInfo>, supportCustomDomains: boolean) {
  let setupDomainMessage = '';

  if (supportCustomDomains) {
    setupDomainMessage = `\n\n${getSetupDomainMessage(info)}`;
  }

  if (info.kind === 'netlify') {
    const { owner, user_credentials: userCredentials, site_url: siteUrl, claim_url: claimUrl } = info;

    if (owner === 'stackblitz') {
      const message = `Your project has been previously published to ${siteUrl} and is owned by the Bolt Netlify account.`;

      if (userCredentials === 'valid') {
        // this means that the project exists on the StackBlitz account, but the user already set credentials
        return `${message}\n\nIt looks like you have connected your personal Netlify account. In order to be able to keep updating the live website, make sure to claim ownership by visiting ${claimUrl}.${setupDomainMessage}`;
      }

      if (userCredentials === 'expired') {
        // this means that the project exists on the StackBlitz account, and the user has expired credentials
        return `${message}\n\nIt looks like you have connected your personal Netlify account, but the token is expired. If you want to publish this project to your personal account, claim ownership by visiting ${claimUrl} and [reconnect with Netlify](#__bolt-settings__/applications).${setupDomainMessage}`;
      }

      // this means that the project exists on the StackBlitz account, but the user has not set credentials
      return `${message}\n\nIf you want to publish this project to your personal account, claim ownership by visiting ${claimUrl} and [connect with Netlify](#__bolt-settings__/applications).${setupDomainMessage}`;
    }

    if (owner === 'unknown') {
      const message = `Your project has been previously published to ${siteUrl} and is owned by a Netlify account which does not match the currently connected Netlify account.`;

      if (userCredentials === 'valid') {
        // this means the project was claimed by an account that is not the user's
        return `${message}\n\nIf you want to update the currently published website, you can [open settings](#__bolt-settings__/applications) and connect with another Netlify account.${setupDomainMessage}`;
      }

      if (userCredentials === 'expired') {
        // this means the project most likely exists on the user's Netlify account, but the token is expired
        return `${message}\n\nHowever, we detected an expired Netlify token. In order to be able to update the live website, [open settings](#__bolt-settings__/applications) and reconnect with Netlify.${setupDomainMessage}`;
      }

      // this means the project most likely exists on the user's Netlify account
      return `Your project has been previously published to ${siteUrl}.\n\nIn order to be able to update the live website, [open settings](#__bolt-settings__/applications) and connect with Netlify.${setupDomainMessage}`;
    }

    return `Your project has been previously published to ${siteUrl} and is owned by your personal Netlify account.${setupDomainMessage}`;
  }

  return `Your project has been previously published to ${info.site_url} using Bolt Hosting.${setupDomainMessage}`;
}

function getSetupDomainMessage(info: ProjectDeploymentInfo) {
  if (!info || info.kind === 'netlify') {
    // TODO we don't show anything anymore for Netlify, should we ask them to switch to Bolt Hosting?
    return '';
  }

  if (!info.site_url.endsWith('.bolt.host') && !info.site_url.endsWith('.bolthost.dev')) {
    return '';
  }

  // default response when we use bolt static hosting
  return `You can now [set up a custom domain](#__bolt-settings__/domains) for your project.`;
}
